import { FABRIC_BLEND_MODE_MAP } from 'js/config/blendModeOptions';
import { BlendMode } from 'js/types';

import BlendModeCompositor from './BlendModeCompositor';
import {
  ALPHA_CHANNEL_OFFSET,
  MAX_COLOR_CHANNEL_VALUE,
  RGB_CHANNEL_COUNT,
  RGBA_CHANNEL_COUNT
} from './blendModeConstants';

const HALF_DIMENSION = 0.5;
const SHADOW_BLUR_PADDING_MULTIPLIER = 2;
const ANTIALIAS_PADDING_PIXELS = 2;

interface BlendBounds {
  width?: number;
  height?: number;
  strokeWidth?: number;
  fontSize?: number;
  radius?: number;
  rx?: number;
  ry?: number;
}

// Weak keys let buffers be collected when their Fabric elements are discarded.
const buffers = new WeakMap<BlendBounds, CanvasRenderingContext2D>();
let compositor: BlendModeCompositor | undefined;

const isCustomBlendMode = (mode: BlendMode) => mode === BlendMode.Add || mode === BlendMode.Subtract;

export const getBlendModeProperties = (mode: BlendMode) => ({
  // Custom modes need the live backdrop, which is absent from Fabric object caches.
  objectCaching: !isCustomBlendMode(mode),
  globalCompositeOperation: isCustomBlendMode(mode) ? 'source-over' : (FABRIC_BLEND_MODE_MAP[mode] ?? 'source-over')
});

/**
 * Renders in Fabric's current transform, then blends in backing-store coordinates.
 * Bounds identify both the local drawing extents and the reusable element buffer.
 * Omitting bounds uses the whole canvas as a conservative fallback.
 */
export const renderWithBlendMode = (
  ctx: CanvasRenderingContext2D,
  mode: BlendMode,
  render: (context: CanvasRenderingContext2D) => void,
  bounds?: BlendBounds
) => {
  if (!isCustomBlendMode(mode)) {
    render(ctx);
    return;
  }

  const transform = ctx.getTransform();
  let left = 0;
  let top = 0;
  let right = ctx.canvas.width;
  let bottom = ctx.canvas.height;
  if (bounds) {
    // Include stroke and text overhang before transforming into backing-store pixels.
    const padding = (bounds.strokeWidth ?? 0) + (bounds.fontSize ?? 0);
    const halfWidth = Math.max((bounds.width ?? 0) * HALF_DIMENSION, bounds.radius ?? 0, bounds.rx ?? 0) + padding;
    const halfHeight = Math.max((bounds.height ?? 0) * HALF_DIMENSION, bounds.radius ?? 0, bounds.ry ?? 0) + padding;
    // Absolute matrix components bound all four corners, including skew and flips.
    const extentX = Math.abs(transform.a) * halfWidth + Math.abs(transform.c) * halfHeight;
    const extentY = Math.abs(transform.b) * halfWidth + Math.abs(transform.d) * halfHeight;
    const shadowPadding = ctx.shadowBlur * SHADOW_BLUR_PADDING_MULTIPLIER;
    // Round outwards and retain antialiased edge pixels before clipping to the canvas.
    left = Math.max(
      0,
      Math.floor(transform.e - extentX - shadowPadding + Math.min(0, ctx.shadowOffsetX)) - ANTIALIAS_PADDING_PIXELS
    );
    top = Math.max(
      0,
      Math.floor(transform.f - extentY - shadowPadding + Math.min(0, ctx.shadowOffsetY)) - ANTIALIAS_PADDING_PIXELS
    );
    right = Math.min(
      right,
      Math.ceil(transform.e + extentX + shadowPadding + Math.max(0, ctx.shadowOffsetX)) + ANTIALIAS_PADDING_PIXELS
    );
    bottom = Math.min(
      bottom,
      Math.ceil(transform.f + extentY + shadowPadding + Math.max(0, ctx.shadowOffsetY)) + ANTIALIAS_PADDING_PIXELS
    );
  }
  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return;

  let source = bounds ? buffers.get(bounds) : undefined;
  if (!source) {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) throw new Error('Unable to create blend mode rendering context');
    source = context;
    if (bounds) buffers.set(bounds, source);
  }
  const buffer = source.canvas;
  // Assigning canvas dimensions clears context state, so resize only when needed.
  if (buffer.width !== width) buffer.width = width;
  if (buffer.height !== height) buffer.height = height;
  source.clearRect(0, 0, width, height);
  source.save();
  try {
    // Preserve all live transforms, offset only by the cropped buffer's origin.
    source.setTransform(transform.a, transform.b, transform.c, transform.d, transform.e - left, transform.f - top);
    source.imageSmoothingEnabled = ctx.imageSmoothingEnabled;
    render(source);
  } finally {
    source.restore();
  }

  compositor ??= new BlendModeCompositor();
  if (compositor.render(ctx, buffer, mode, left, top)) return;

  // Compatibility path: mirror the shader in byte-valued, unpremultiplied RGBA.
  const pixels = source.getImageData(0, 0, width, height);
  const backdrop = ctx.getImageData(left, top, width, height);
  for (let i = 0; i < pixels.data.length; i += RGBA_CHANNEL_COUNT) {
    if (pixels.data[i + ALPHA_CHANNEL_OFFSET] === 0) continue;
    const backdropAlpha = backdrop.data[i + ALPHA_CHANNEL_OFFSET] / MAX_COLOR_CHANNEL_VALUE;
    for (let channel = 0; channel < RGB_CHANNEL_COUNT; channel++) {
      const src = pixels.data[i + channel];
      const dst = backdrop.data[i + channel];
      const blended = mode === BlendMode.Add ? Math.min(MAX_COLOR_CHANNEL_VALUE, dst + src) : Math.max(0, dst - src);
      // Weight by backdrop coverage; drawImage applies source coverage and opacity.
      pixels.data[i + channel] = (1 - backdropAlpha) * src + backdropAlpha * blended;
    }
  }

  source.putImageData(pixels, 0, 0);
  ctx.save();
  try {
    ctx.resetTransform();
    // drawImage, unlike putImageData, respects Fabric's opacity and clipping.
    ctx.drawImage(buffer, left, top);
  } finally {
    ctx.restore();
  }
};
